import { type CSSProperties, type ReactNode, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageHeader, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import { useEndpoints } from '../hooks/useEndpoints';
import { useDeleteEndpoint } from '../hooks/useDeleteEndpoint';
import { useInvoke } from '../hooks/useInvoke';
import { useCreateEndpoint } from '../hooks/useCreateEndpoint';
import { useUpdateEndpoint } from '../hooks/useUpdateEndpoint';
import { InvokeResultDrawer } from '../components/InvokeResultDrawer';
import { IntegrationCommonUsagePanel } from '../components/IntegrationCommonUsagePanel';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { endpointFormSchema } from '../schema/integrationHub.schema';
import type {
  IntegrationEndpointDto,
  IntegrationProtocol,
  InvokeResultDto,
  ApiError,
  EndpointListFilter,
} from '../types/integrationHub';
import type { EndpointFormValues } from '../schema/integrationHub.schema';

const DEFAULT_PAGE_SIZE = 20;

function SkeletonRow({ cols }: { cols: number }) {
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => (
        <td
          key={i}
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <div
            style={{
              height: 12,
              width: `${50 + (i * 13) % 40}%`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

/**
 * IntegrationHubPage — 연동 엔드포인트 목록 + 호출(Invoke) (/integration-hub)
 * 5상태: loading / empty / error / default / dialog(삭제확인 + 엔드포인트 폼 + InvokeResultDrawer)
 */
export function IntegrationHubPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // 필터 상태
  const [protocolFilter, setProtocolFilter] = useState<IntegrationProtocol | ''>('');
  const [enabledFilter, setEnabledFilter] = useState<boolean | ''>('');
  const [page, setPage] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState<EndpointListFilter>({
    protocol: '',
    enabled: '',
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    sort: 'createdAt,desc',
  });

  // dialog/drawer 상태
  const [invokeTarget, setInvokeTarget] = useState<IntegrationEndpointDto | null>(null);
  const [invokeResult, setInvokeResult] = useState<InvokeResultDto | null>(null);
  const [invokeError, setInvokeError] = useState<ApiError | null>(null);
  const [showInvokeDrawer, setShowInvokeDrawer] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<IntegrationEndpointDto | null>(null);
  const [formTarget, setFormTarget] = useState<IntegrationEndpointDto | 'new' | null>(null);

  const [toast, setToast] = useState<string | null>(null);

  // 쿼리
  const listQuery = useEndpoints(appliedFilters);
  const deleteMutation = useDeleteEndpoint();
  const invokeMutation = useInvoke();
  const createMutation = useCreateEndpoint();
  const updateMutation = useUpdateEndpoint();

  const pageData = listQuery.data;
  const isListLoading = listQuery.isLoading;
  const isListError = listQuery.isError;
  const isEmpty = !isListLoading && !isListError && (pageData?.empty ?? false);
  const showTable = !isListLoading && !isListError && !isEmpty && !!pageData;

  // 폼
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<EndpointFormValues>({
    resolver: zodResolver(endpointFormSchema),
    defaultValues: {
      enabled: true,
      propagateAuth: true,
    },
  });

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }, []);

  const handleSearch = useCallback(() => {
    setPage(0);
    setAppliedFilters({
      protocol: protocolFilter,
      enabled: enabledFilter,
      page: 0,
      size: DEFAULT_PAGE_SIZE,
      sort: 'createdAt,desc',
    });
  }, [protocolFilter, enabledFilter]);

  const handleReset = useCallback(() => {
    setProtocolFilter('');
    setEnabledFilter('');
    setPage(0);
    setAppliedFilters({ protocol: '', enabled: '', page: 0, size: DEFAULT_PAGE_SIZE, sort: 'createdAt,desc' });
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    setAppliedFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleInvoke = useCallback(
    (endpoint: IntegrationEndpointDto) => {
      setInvokeTarget(endpoint);
      setInvokeResult(null);
      setInvokeError(null);
      setShowInvokeDrawer(true);
      invokeMutation.mutate(
        { id: endpoint.id, req: {} },
        {
          onSuccess: (data) => {
            setInvokeResult(data);
            setInvokeError(null);
          },
          onError: (err) => {
            setInvokeError(err as ApiError);
            setInvokeResult(null);
          },
        }
      );
    },
    [invokeMutation]
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        showToast(t('integrationHub.toast.deleted'));
      },
      onError: (err) => {
        setDeleteTarget(null);
        const apiErr = err as unknown as ApiError;
        showToast(
          t(`integrationHub.error.${apiErr.code}`, { defaultValue: t('integrationHub.error.network') })
        );
      },
    });
  }, [deleteTarget, deleteMutation, showToast, t]);

  const openCreateForm = useCallback(() => {
    setFormTarget('new');
    reset({ enabled: true, propagateAuth: true });
  }, [reset]);

  const openEditForm = useCallback((endpoint: IntegrationEndpointDto) => {
    setFormTarget(endpoint);
    setValue('code', endpoint.code);
    setValue('name', endpoint.name);
    setValue('protocol', endpoint.protocol as 'HTTP' | 'S3' | 'SFTP');
    setValue('targetUrl', endpoint.targetUrl ?? '');
    setValue('httpMethod', endpoint.httpMethod ?? '');
    setValue('path', endpoint.path ?? '');
    setValue('description', endpoint.description ?? '');
    setValue('enabled', endpoint.enabled);
    setValue('propagateAuth', endpoint.propagateAuth);
  }, [setValue]);

  const onFormSubmit = useCallback(
    (values: EndpointFormValues) => {
      if (formTarget === 'new') {
        createMutation.mutate(
          {
            code: values.code,
            name: values.name,
            protocol: values.protocol as IntegrationProtocol,
            targetUrl: values.targetUrl ?? null,
            httpMethod: values.httpMethod ?? null,
            path: values.path ?? null,
            description: values.description ?? null,
            enabled: values.enabled,
            propagateAuth: values.propagateAuth,
          },
          {
            onSuccess: () => {
              setFormTarget(null);
              showToast(t('integrationHub.toast.saved'));
            },
            onError: (err) => {
              const apiErr = err as unknown as ApiError;
              showToast(t(`integrationHub.error.${apiErr.code}`, { defaultValue: t('integrationHub.error.network') }));
            },
          }
        );
      } else if (formTarget && typeof formTarget === 'object') {
        updateMutation.mutate(
          {
            id: formTarget.id,
            req: {
              name: values.name,
              protocol: values.protocol as IntegrationProtocol,
              targetUrl: values.targetUrl ?? null,
              httpMethod: values.httpMethod ?? null,
              path: values.path ?? null,
              description: values.description ?? null,
              enabled: values.enabled,
              propagateAuth: values.propagateAuth,
            },
          },
          {
            onSuccess: () => {
              setFormTarget(null);
              showToast(t('integrationHub.toast.saved'));
            },
            onError: (err) => {
              const apiErr = err as unknown as ApiError;
              showToast(t(`integrationHub.error.${apiErr.code}`, { defaultValue: t('integrationHub.error.network') }));
            },
          }
        );
      }
    },
    [formTarget, createMutation, updateMutation, showToast, t]
  );

  const TABLE_HEADERS = useMemo(
    () => [
      t('integrationHub.endpoint.column.code'),
      t('integrationHub.endpoint.column.protocol'),
      t('integrationHub.endpoint.column.targetUrl'),
      t('integrationHub.endpoint.column.enabled'),
      t('integrationHub.endpoint.column.actions'),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language]
  );

  const isMutating = createMutation.isPending || updateMutation.isPending;

  // 403 확인
  const is403 = isListError && (listQuery.error as unknown as ApiError)?.code === 'AUTH-403-001';

  return (
    <div style={{ padding: 0 }}>
      <PageHeader
        title={t('integrationHub.title')}
        breadcrumb={<span>{t('integrationHub.breadcrumb')}</span>}
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => navigate('/integration-hub/storage')}
              style={tabButtonStyle}
            >
              {t('integrationHub.tab.storage')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/integration-hub/history')}
              style={tabButtonStyle}
            >
              {t('integrationHub.tab.history')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/integration-hub/tcp')}
              style={tabButtonStyle}
            >
              {t('integrationHub.tab.tcp')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/integration-hub/grpc')}
              style={tabButtonStyle}
            >
              {t('integrationHub.tab.grpc')}
            </button>
            <ButtonGuard requirePermission="INTEGRATION_MANAGE">
              {({ disabled, reason }) => (
                <button
                  type="button"
                  disabled={disabled}
                  title={reason}
                  onClick={openCreateForm}
                  style={{
                    ...primaryButtonStyle,
                    background: disabled ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
                    color: disabled ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.6 : 1,
                  }}
                >
                  {t('integrationHub.endpoint.button.add')}
                </button>
              )}
            </ButtonGuard>
          </div>
        }
      />

      {/* demo.mode 배너 — pageData 있을 때 */}
      {pageData && (
        <div
          role="status"
          aria-live="polite"
          style={{
            padding: 'var(--spacing-2) var(--spacing-4)',
            background: 'var(--color-surface-muted)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
            marginBottom: 'var(--spacing-3)',
          }}
        >
          {t('integrationHub.demoMode.on')}
        </div>
      )}

      {/* 필터 바 */}
      <div
        role="search"
        aria-label={t('integrationHub.endpoint.title')}
        style={filterBarStyle}
      >
        <label style={filterLabelStyle}>
          {t('integrationHub.endpoint.filter.protocol')}
          <select
            value={protocolFilter}
            onChange={(e) => setProtocolFilter(e.target.value as IntegrationProtocol | '')}
            style={selectStyle}
          >
            <option value="">{t('integrationHub.endpoint.filter.all')}</option>
            <option value="HTTP">{t('integrationHub.protocol.HTTP')}</option>
            <option value="S3">{t('integrationHub.protocol.S3')}</option>
            <option value="SFTP">{t('integrationHub.protocol.SFTP')}</option>
          </select>
        </label>
        <label style={filterLabelStyle}>
          {t('integrationHub.endpoint.filter.enabled')}
          <select
            value={String(enabledFilter)}
            onChange={(e) => {
              const v = e.target.value;
              setEnabledFilter(v === '' ? '' : v === 'true');
            }}
            style={selectStyle}
          >
            <option value="">{t('integrationHub.endpoint.filter.all')}</option>
            <option value="true">{t('integrationHub.endpoint.filter.active')}</option>
            <option value="false">{t('integrationHub.endpoint.filter.inactive')}</option>
          </select>
        </label>
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginLeft: 'auto' }}>
          <button
            type="button"
            onClick={handleSearch}
            disabled={isListLoading}
            style={primaryButtonStyle}
          >
            {t('integrationHub.endpoint.button.search')}
          </button>
          <button
            type="button"
            onClick={handleReset}
            style={secondaryButtonStyle}
          >
            {t('integrationHub.endpoint.button.reset')}
          </button>
        </div>
      </div>

      {/* 5상태 콘텐츠 영역 */}
      <div
        style={{
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          background: 'var(--color-surface-background)',
        }}
      >
        {/* loading skeleton */}
        {isListLoading && (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            aria-label={t('integrationHub.endpoint.loadingAriaLabel')}
            aria-busy="true"
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {TABLE_HEADERS.map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} cols={TABLE_HEADERS.length} />
              ))}
            </tbody>
          </table>
        )}

        {/* error */}
        {isListError && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            {is403 ? (
              <ErrorState
                title={t('integrationHub.error.AUTH-403-001')}
                message={t('integrationHub.error.forbidden')}
                onRetry={() => navigate('/')}
              />
            ) : (
              <ErrorState
                title={t('integrationHub.error.loadFailed')}
                message={
                  (listQuery.error as unknown as ApiError)?.message ??
                  t('integrationHub.error.network')
                }
                onRetry={() => void listQuery.refetch()}
              />
            )}
          </div>
        )}

        {/* empty */}
        {isEmpty && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <EmptyState
              title={t('integrationHub.endpoint.empty.title')}
              description={t('integrationHub.endpoint.empty.description')}
              action={
                <ButtonGuard requirePermission="INTEGRATION_MANAGE">
                  {({ disabled, reason }) => (
                    <button
                      type="button"
                      disabled={disabled}
                      title={reason}
                      onClick={openCreateForm}
                      style={{
                        ...primaryButtonStyle,
                        background: disabled ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
                        color: disabled ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        opacity: disabled ? 0.6 : 1,
                      }}
                    >
                      {t('integrationHub.endpoint.button.add')}
                    </button>
                  )}
                </ButtonGuard>
              }
            />
          </div>
        )}

        {/* default — DataTable */}
        {showTable && pageData && (
          <>
            {pageData.totalElements > 0 && (
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', padding: 'var(--spacing-2) var(--spacing-4)', margin: 0 }}>
                {t('integrationHub.endpoint.totalCount', { count: pageData.totalElements })}
              </p>
            )}
            <div role="region" aria-label={t('integrationHub.endpoint.tableAriaLabel')} style={{ overflowX: 'auto' }}>
              <table
                style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
                aria-label={t('integrationHub.endpoint.tableAriaLabel')}
              >
                <thead>
                  <tr style={{ background: 'var(--color-surface-muted)' }}>
                    {TABLE_HEADERS.map((h) => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageData.content.map((row) => (
                    <tr
                      key={row.id}
                      aria-label={t('integrationHub.endpoint.rowAriaLabel', { code: row.code })}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background = 'var(--color-surface-muted)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background = '';
                      }}
                    >
                      <td style={tdStyle}>
                        <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-sm)' }}>
                          {row.code}
                        </code>
                      </td>
                      <td style={tdStyle}>
                        <StatusPill
                          tone={
                            row.protocol === 'HTTP'
                              ? 'info'
                              : row.protocol === 'S3'
                              ? 'success'
                              : 'warning'
                          }
                        >
                          {t(`integrationHub.protocol.${row.protocol}`)}
                        </StatusPill>
                      </td>
                      <td style={{ ...tdStyle, color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
                        {row.protocol === 'HTTP' ? (row.targetUrl ?? '-') : '(backend 설정 참조)'}
                      </td>
                      <td style={tdStyle}>
                        {row.enabled ? (
                          <span style={{ color: 'var(--color-success)' }}>✓</span>
                        ) : (
                          <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 'var(--spacing-1)' }}>
                          {/* 호출 버튼 */}
                          <ButtonGuard requirePermission="INTEGRATION_INVOKE">
                            {({ disabled, reason }) => (
                              <button
                                type="button"
                                disabled={disabled || invokeMutation.isPending}
                                title={reason}
                                onClick={() => handleInvoke(row)}
                                aria-label={t('integrationHub.endpoint.button.invoke')}
                                style={{
                                  ...actionButtonStyle,
                                  background: disabled ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
                                  color: disabled ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                                  border: 'none',
                                  opacity: disabled || invokeMutation.isPending ? 0.6 : 1,
                                  cursor: disabled || invokeMutation.isPending ? 'not-allowed' : 'pointer',
                                }}
                              >
                                {invokeMutation.isPending && invokeTarget?.id === row.id
                                  ? '...'
                                  : t('integrationHub.endpoint.button.invoke')}
                              </button>
                            )}
                          </ButtonGuard>

                          {/* 수정 버튼 */}
                          <ButtonGuard requirePermission="INTEGRATION_MANAGE">
                            {({ disabled, reason }) => (
                              <button
                                type="button"
                                disabled={disabled}
                                title={reason}
                                onClick={() => openEditForm(row)}
                                style={{
                                  ...actionButtonStyle,
                                  opacity: disabled ? 0.5 : 1,
                                  cursor: disabled ? 'not-allowed' : 'pointer',
                                }}
                              >
                                {t('integrationHub.endpoint.button.edit')}
                              </button>
                            )}
                          </ButtonGuard>

                          {/* 삭제 버튼 */}
                          <ButtonGuard requirePermission="INTEGRATION_MANAGE">
                            {({ disabled, reason }) => (
                              <button
                                type="button"
                                disabled={disabled}
                                title={reason}
                                onClick={() => setDeleteTarget(row)}
                                style={{
                                  ...actionButtonStyle,
                                  border: `1px solid ${disabled ? 'var(--color-surface-border)' : 'var(--color-danger)'}`,
                                  color: disabled ? 'var(--color-text-muted)' : 'var(--color-danger)',
                                  opacity: disabled ? 0.5 : 1,
                                  cursor: disabled ? 'not-allowed' : 'pointer',
                                }}
                              >
                                {t('integrationHub.endpoint.button.delete')}
                              </button>
                            )}
                          </ButtonGuard>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 페이지네이션 */}
            <div style={paginationBarStyle}>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                {t('integrationHub.endpoint.pagination.showing', {
                  total: pageData.totalElements,
                  from: pageData.number * pageData.size + 1,
                  to: Math.min((pageData.number + 1) * pageData.size, pageData.totalElements),
                })}
              </span>
              <nav aria-label={t('integrationHub.endpoint.pagination.nav')} style={{ display: 'inline-flex', gap: 'var(--spacing-1)' }}>
                <button
                  type="button"
                  disabled={pageData.first}
                  onClick={() => handlePageChange(page - 1)}
                  aria-label={t('integrationHub.endpoint.pagination.prev')}
                  style={paginationButtonStyle(pageData.first)}
                >
                  &lt;
                </button>
                {Array.from({ length: Math.min(pageData.totalPages, 10) }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handlePageChange(i)}
                    aria-label={t('integrationHub.endpoint.pagination.page', { page: i + 1 })}
                    aria-current={i === pageData.number ? 'page' : undefined}
                    style={paginationPageStyle(i === pageData.number)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={pageData.last}
                  onClick={() => handlePageChange(page + 1)}
                  aria-label={t('integrationHub.endpoint.pagination.next')}
                  style={paginationButtonStyle(pageData.last)}
                >
                  &gt;
                </button>
              </nav>
            </div>
          </>
        )}
      </div>

      {/* 공통 모듈 패널 */}
      <IntegrationCommonUsagePanel />

      {/* InvokeResultDrawer (dialog 상태) */}
      {showInvokeDrawer && (
        <InvokeResultDrawer
          result={invokeResult}
          error={invokeError}
          isPending={invokeMutation.isPending}
          onClose={() => {
            setShowInvokeDrawer(false);
            setInvokeResult(null);
            setInvokeError(null);
            setInvokeTarget(null);
          }}
          onCopyTrace={(traceId) => {
            void navigator.clipboard.writeText(traceId);
            showToast(t('integrationHub.toast.traceCopied'));
          }}
        />
      )}

      {/* 삭제 confirm dialog (dialog 상태) */}
      {deleteTarget && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-endpoint-title"
          style={dialogOverlayStyle}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setDeleteTarget(null);
          }}
        >
          <div style={dialogContentStyle}>
            <h2 id="delete-endpoint-title" style={dialogTitleStyle}>
              {t('integrationHub.delete.title')}
            </h2>
            <p style={dialogBodyStyle}>{t('integrationHub.delete.body')}</p>
            <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 'var(--spacing-5)' }}>
              "{deleteTarget.code}"
            </p>
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={deleteMutation.isPending}
                style={secondaryButtonStyle}
              >
                {t('integrationHub.delete.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleteMutation.isPending}
                autoFocus
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-danger)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer',
                  opacity: deleteMutation.isPending ? 0.6 : 1,
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                {deleteMutation.isPending ? '...' : t('integrationHub.delete.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 엔드포인트 폼 dialog (dialog 상태) */}
      {formTarget !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="endpoint-form-title"
          style={dialogOverlayStyle}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setFormTarget(null);
          }}
        >
          <div style={{ ...dialogContentStyle, width: 520 }}>
            <h2 id="endpoint-form-title" style={dialogTitleStyle}>
              {formTarget === 'new'
                ? t('integrationHub.endpoint.form.title.create')
                : t('integrationHub.endpoint.form.title.edit')}
            </h2>
            <form onSubmit={handleSubmit(onFormSubmit)}>
              {/* code (생성 시만 편집 가능) */}
              <FormField
                label={t('integrationHub.endpoint.form.field.code')}
                error={errors.code?.message}
                required
              >
                <input
                  type="text"
                  placeholder={t('integrationHub.endpoint.form.placeholder.code')}
                  disabled={formTarget !== 'new'}
                  aria-invalid={!!errors.code}
                  aria-describedby={errors.code ? 'code-error' : undefined}
                  style={{ ...inputStyle, opacity: formTarget !== 'new' ? 0.6 : 1 }}
                  {...register('code')}
                />
                {errors.code && <span id="code-error" role="alert" style={errorTextStyle}>{errors.code.message}</span>}
              </FormField>

              <FormField
                label={t('integrationHub.endpoint.form.field.name')}
                error={errors.name?.message}
                required
              >
                <input
                  type="text"
                  placeholder={t('integrationHub.endpoint.form.placeholder.name')}
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'name-error' : undefined}
                  style={inputStyle}
                  {...register('name')}
                />
                {errors.name && <span id="name-error" role="alert" style={errorTextStyle}>{errors.name.message}</span>}
              </FormField>

              <FormField label={t('integrationHub.endpoint.form.field.protocol')} required>
                <select
                  aria-invalid={!!errors.protocol}
                  style={inputStyle}
                  {...register('protocol')}
                >
                  <option value="HTTP">{t('integrationHub.protocol.HTTP')}</option>
                  <option value="S3">{t('integrationHub.protocol.S3')}</option>
                  <option value="SFTP">{t('integrationHub.protocol.SFTP')}</option>
                </select>
              </FormField>

              <FormField label={t('integrationHub.endpoint.form.field.targetUrl')}>
                <input
                  type="url"
                  placeholder={t('integrationHub.endpoint.form.placeholder.targetUrl')}
                  style={inputStyle}
                  {...register('targetUrl')}
                />
              </FormField>

              <FormField label={t('integrationHub.endpoint.form.field.httpMethod')}>
                <select style={inputStyle} {...register('httpMethod')}>
                  <option value="">선택</option>
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                  <option value="DELETE">DELETE</option>
                </select>
              </FormField>

              <FormField label={t('integrationHub.endpoint.form.field.path')}>
                <input
                  type="text"
                  placeholder={t('integrationHub.endpoint.form.placeholder.path')}
                  style={inputStyle}
                  {...register('path')}
                />
              </FormField>

              <FormField label={t('integrationHub.endpoint.form.field.description')}>
                <textarea
                  rows={2}
                  style={{ ...inputStyle, resize: 'vertical' }}
                  {...register('description')}
                />
              </FormField>

              <div style={{ display: 'flex', gap: 'var(--spacing-4)', marginBottom: 'var(--spacing-4)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>
                  <input type="checkbox" {...register('enabled')} />
                  {t('integrationHub.endpoint.form.field.enabled')}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}>
                  <input type="checkbox" {...register('propagateAuth')} />
                  {t('integrationHub.endpoint.form.field.propagateAuth')}
                </label>
              </div>

              <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setFormTarget(null)}
                  disabled={isMutating}
                  style={secondaryButtonStyle}
                >
                  {t('integrationHub.endpoint.form.button.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isMutating}
                  style={{
                    ...primaryButtonStyle,
                    opacity: isMutating ? 0.6 : 1,
                    cursor: isMutating ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isMutating ? '...' : t('integrationHub.endpoint.form.button.submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 토스트 */}
      {toast !== null && (
        <div
          role="alert"
          aria-live="polite"
          style={toastStyle}
        >
          {toast}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 768px) {
          table { min-width: 600px; }
        }
      `}</style>
    </div>
  );
}

function FormField({
  label,
  error,
  required,
  children,
}: {
  label: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', marginBottom: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
      {label}
      {required && <span style={{ color: 'var(--color-danger)', marginLeft: 2 }}>*</span>}
      {children}
      {error && <span role="alert" style={errorTextStyle}>{error}</span>}
    </label>
  );
}

// Shared styles
const tabButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--spacing-2) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const primaryButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-brand-primary)',
  color: 'var(--color-text-inverse)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const secondaryButtonStyle: CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const actionButtonStyle: CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-2)',
  background: 'transparent',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--font-size-xs)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const filterBarStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--spacing-3)',
  padding: 'var(--spacing-3) var(--spacing-4)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-muted)',
  marginBottom: 'var(--spacing-4)',
  alignItems: 'flex-end',
};

const filterLabelStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-1)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
};

const selectStyle: CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  cursor: 'pointer',
};

const inputStyle: CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-sans)',
  width: '100%',
};

const thStyle: CSSProperties = {
  textAlign: 'left',
  padding: 'var(--spacing-3) var(--spacing-4)',
  borderBottom: '1px solid var(--color-surface-border)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const tdStyle: CSSProperties = {
  padding: 'var(--spacing-3) var(--spacing-4)',
  borderBottom: '1px solid var(--color-surface-border)',
  color: 'var(--color-text-primary)',
};

const paginationBarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: 'var(--spacing-3) var(--spacing-4)',
  background: 'var(--color-surface-muted)',
  borderTop: '1px solid var(--color-surface-border)',
  flexWrap: 'wrap',
  gap: 'var(--spacing-2)',
};

function paginationButtonStyle(disabled: boolean): CSSProperties {
  return {
    minWidth: 28,
    height: 28,
    padding: '0 var(--spacing-2)',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-surface-border)',
    fontSize: 'var(--font-size-xs)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
  };
}

function paginationPageStyle(active: boolean): CSSProperties {
  return {
    minWidth: 28,
    height: 28,
    padding: '0 var(--spacing-2)',
    borderRadius: 'var(--radius-sm)',
    background: active ? 'var(--color-brand-primary)' : 'var(--color-surface-elevated)',
    border: `1px solid ${active ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
    color: active ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
    fontSize: 'var(--font-size-xs)',
    cursor: 'pointer',
    fontWeight: active ? 600 : 400,
  };
}

const dialogOverlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0,0,0,0.45)',
};

const dialogContentStyle: CSSProperties = {
  background: 'var(--color-surface-background)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-6)',
  width: 420,
  maxWidth: '90vw',
  boxShadow: 'var(--shadow-xl)',
  maxHeight: '90vh',
  overflowY: 'auto',
};

const dialogTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-lg)',
  fontWeight: 700,
  marginBottom: 'var(--spacing-3)',
  color: 'var(--color-text-primary)',
};

const dialogBodyStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  marginBottom: 'var(--spacing-5)',
};

const errorTextStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-danger)',
  marginTop: 'var(--spacing-1)',
};

const toastStyle: CSSProperties = {
  position: 'fixed',
  bottom: 'var(--spacing-6)',
  right: 'var(--spacing-6)',
  zIndex: 1100,
  background: 'var(--color-surface-overlay, #1e1e2e)',
  color: 'var(--color-text-inverse)',
  padding: 'var(--spacing-3) var(--spacing-5)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  boxShadow: 'var(--shadow-lg)',
  maxWidth: 360,
};
